import React, { useCallback, useMemo } from 'react';

import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Stack,
  TablePagination,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/system';

import SbButton from '@Components/Button';
import DateRangePicker from '@Components/DateRangePicker';
import FilterSelect from '@Components/Filter/Dropdown';
import FilterSearch from '@Components/Filter/Search';

import { SHIPMENT_STATUS } from '@Constants/Shipment';
import { ShipmentObj } from '@Reducers/Invoice/Type';

import { convertWeightFormKGtoLB } from '../../../../utils/convertWeight';
import { formatNumber, useLocale } from '../../../../utils/formatNumber';
import { NonAccountSearchFilterList, ShipmentTypeList, isShipmentSelectable } from '../utils/nonAccountInvoiceUtils';

interface TextWithTooltipProps {
  text: string | number | null | undefined;
  title?: string;
  sx?: object;
  className?: string;
  component?: React.ElementType;
}

const TextWithTooltip = React.memo(function TextWithTooltip({
  text,
  title,
  sx = {},
  className = '',
  component = 'span',
}: TextWithTooltipProps) {
  const displayString = text !== null && text !== undefined && text !== '' ? String(text) : '-';
  const tooltipContent = title || (text ? String(text) : '');

  if (!tooltipContent || tooltipContent === '-') {
    return (
      <Typography
        component={component}
        className={className}
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'block',
          ...sx,
        }}
      >
        {displayString}
      </Typography>
    );
  }

  return (
    <Tooltip title={tooltipContent} arrow enterDelay={250} placement="top">
      <Typography
        component={component}
        className={className}
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'block',
          cursor: 'inherit',
          ...sx,
        }}
      >
        {displayString}
      </Typography>
    </Tooltip>
  );
});

interface ShipmentCardProps {
  shipment: ShipmentObj;
  isSelected: boolean;
  isExpanded: boolean;
  orgName?: string;
  currencySymbol: string;
  onToggleSelect: (id: string, checked: boolean) => void;
  onToggleExpand: (id: string) => void;
}

const ShipmentCard = React.memo(function ShipmentCard({
  shipment,
  isSelected,
  isExpanded,
  orgName,
  currencySymbol,
  onToggleSelect,
  onToggleExpand,
}: ShipmentCardProps) {
  const theme = useTheme();
  const locale = useLocale();

  const selectable = isShipmentSelectable(shipment, orgName);
  const amount = Number(shipment?.totalAmount ?? shipment?.sellingPricing?.totalPricing ?? 0);
  const formattedAmount = formatNumber(amount, locale);

  const displayWeight =
    orgName === 'Cargo Solutions Network'
      ? convertWeightFormKGtoLB(shipment?.sellingPricing?.displayChargeableWeight, orgName)
      : (shipment?.sellingPricing?.displayChargeableWeight || shipment?.sellingPricing?.chargeableWeight || '-') +
        ' kg';

  const packagesCount = shipment?._packageId?.box?.length || shipment?.totalPackages || 0;

  const handleCheckboxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      onToggleSelect(shipment._id, e.target.checked);
    },
    [onToggleSelect, shipment._id],
  );

  const handleExpandClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggleExpand(shipment._id);
    },
    [onToggleExpand, shipment._id],
  );

  return (
    <Box
      sx={{
        borderRadius: '12px',
        border: `1px solid ${isSelected ? alpha(theme.palette.primary.main, 0.45) : theme.palette.divider}`,
        bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.03) : 'background.paper',
        transition: 'all 0.2s ease',
        overflow: 'hidden',
        boxShadow: isSelected ? `0 2px 10px ${alpha(theme.palette.primary.main, 0.08)}` : 'none',
        '&:hover': {
          borderColor: isSelected ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.2),
        },
      }}
    >
      {/* Header Summary Row */}
      <Box
        sx={{
          p: { xs: 1.25, sm: 1.5 },
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          cursor: 'pointer',
        }}
        onClick={() => onToggleExpand(shipment._id)}
      >
        <Checkbox
          checked={isSelected}
          disabled={!selectable}
          onChange={handleCheckboxChange}
          onClick={e => e.stopPropagation()}
          sx={{
            p: 0.5,
            color: selectable ? 'primary.main' : 'text.disabled',
            '&.Mui-disabled': { color: 'text.disabled' },
          }}
        />

        {/* Shipment Details Strip */}
        <Grid container alignItems="center" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
          {/* Shipment Number & Date */}
          <Grid item xs={12} sm={3.5} md={3}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ReceiptOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <TextWithTooltip
                  text={shipment.shipmentNumber}
                  title={`Shipment: ${shipment.shipmentNumber}`}
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.88rem',
                    color: 'text.primary',
                    lineHeight: 1.2,
                  }}
                />
                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.25 }}>
                  {shipment.createdAt ? new Date(shipment.createdAt).toLocaleDateString() : '-'}
                </Typography>
              </Box>
            </Stack>
          </Grid>

          {/* Customer / Bill To */}
          <Grid item xs={12} sm={4} md={3.5}>
            <Box sx={{ minWidth: 0 }}>
              <TextWithTooltip
                text={shipment._userId?.name}
                title={`Bill To: ${shipment._userId?.name || '-'}`}
                sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'text.primary' }}
              />
              <TextWithTooltip
                text={shipment._userId?.email}
                title={shipment._userId?.email || ''}
                sx={{ fontSize: '0.72rem', color: 'text.secondary' }}
              />
            </Box>
          </Grid>

          {/* Status & Route Type */}
          <Grid item xs={6} sm={2.5} md={2.5}>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
              <Chip
                label={SHIPMENT_STATUS[shipment.status]?.label || shipment.status || 'Unknown'}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.68rem',
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: 'primary.main',
                }}
              />
              {orgName !== 'Cargo Solutions Network' && shipment.shipmentRouteType && (
                <Chip
                  label={shipment.shipmentRouteType}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.66rem', textTransform: 'capitalize' }}
                />
              )}
            </Stack>
          </Grid>

          {/* Amount & Expand Toggle */}
          <Grid item xs={6} sm={2} md={3} sx={{ textAlign: 'right' }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
              <Box sx={{ textAlign: 'right' }}>
                <Tooltip title={`Total: ${currencySymbol} ${formattedAmount}`} arrow placement="top">
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: '0.92rem',
                      color: 'primary.main',
                      lineHeight: 1.2,
                    }}
                  >
                    {currencySymbol} {formattedAmount}
                  </Typography>
                </Tooltip>
                {!selectable && (
                  <Typography sx={{ fontSize: '0.65rem', color: 'error.main', fontWeight: 600 }}>
                    Missing AWB
                  </Typography>
                )}
              </Box>

              <IconButton
                size="small"
                onClick={handleExpandClick}
                sx={{
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.25s ease',
                  p: 0.5,
                  color: 'text.secondary',
                }}
                aria-label={isExpanded ? 'Collapse shipment' : 'Expand shipment'}
              >
                <KeyboardArrowDownIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {/* Expandable Details Drawer */}
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Divider />
        <Box sx={{ p: 1.75, bgcolor: alpha(theme.palette.action.hover, 0.2) }}>
          <Grid container spacing={2}>
            {/* Shipper */}
            <Grid item xs={12} sm={6} md={3}>
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <PlaceOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary' }}>
                    SHIPPER
                  </Typography>
                </Stack>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary' }}>
                  {shipment._shipperId?.name || '-'}
                </Typography>
                <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>
                  {[shipment._shipperId?.city, shipment._shipperId?.country].filter(Boolean).join(', ') || '-'}
                </Typography>
              </Stack>
            </Grid>

            {/* Consignee */}
            <Grid item xs={12} sm={6} md={3}>
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <PlaceOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary' }}>
                    CONSIGNEE
                  </Typography>
                </Stack>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary' }}>
                  {shipment._consigneeId?.name || '-'}
                </Typography>
                <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>
                  {[shipment._consigneeId?.city, shipment._consigneeId?.country].filter(Boolean).join(', ') || '-'}
                </Typography>
              </Stack>
            </Grid>

            {/* Weight & Packages */}
            <Grid item xs={6} sm={3} md={3}>
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Inventory2OutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary' }}>
                    WEIGHT & PACKAGES
                  </Typography>
                </Stack>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary' }}>
                  {displayWeight}
                </Typography>
                <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>
                  {packagesCount} {packagesCount === 1 ? 'Package' : 'Packages'}
                </Typography>
              </Stack>
            </Grid>

            {/* Carrier / System */}
            <Grid item xs={6} sm={3} md={3}>
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <LocalShippingOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary' }}>
                    CARRIER & SYSTEM
                  </Typography>
                </Stack>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary' }}>
                  {shipment._chargingSystemId?.name || (shipment as any)?.system?.name || '-'}
                </Typography>
                <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>
                  AWB: {shipment.carrierDetails?.awb || '-'}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Box>
  );
});

export interface NonAccountShipmentSectionProps {
  list: ShipmentObj[];
  totalCount: number;
  currentPage: number;
  perPage: number;
  loading: boolean;
  systemList: { display: string; value: string }[];
  filterSelectValue: string;
  filterText: string;
  dateRange: { startDate: Date; endDate: Date };
  system: string[];
  shipmentType: string[];
  selectedShipmentIds: string[];
  expandedShipments: Record<string, boolean>;
  orgName?: string;
  primaryCurrencySymbol: string;
  onSearchSelectChange: (val: string) => void;
  onSearchTextChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSearchTextSubmit: () => void;
  onTextClear: () => void;
  onDateChange: (range: { startDate: Date; endDate: Date }, isOpen: boolean) => void;
  onSystemFilter: (sys: string[]) => void;
  onShipmentTypeFilter: (types: string[]) => void;
  onClearFilters: () => void;
  onPageChange: (e: unknown, newPage: number) => void;
  onRowsPerPageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectAllShipments: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onToggleSelectShipment: (id: string, checked: boolean) => void;
  onToggleExpandShipment: (id: string) => void;
}

const NonAccountShipmentSection = React.memo(function NonAccountShipmentSection(props: NonAccountShipmentSectionProps) {
  const {
    list,
    totalCount,
    currentPage,
    perPage,
    loading,
    systemList,
    filterSelectValue,
    filterText,
    dateRange,
    system,
    shipmentType,
    selectedShipmentIds,
    expandedShipments,
    orgName,
    primaryCurrencySymbol,
    onSearchSelectChange,
    onSearchTextChange,
    onSearchTextSubmit,
    onTextClear,
    onDateChange,
    onSystemFilter,
    onShipmentTypeFilter,
    onClearFilters,
    onPageChange,
    onRowsPerPageChange,
    onSelectAllShipments,
    onToggleSelectShipment,
    onToggleExpandShipment,
  } = props;

  const theme = useTheme();

  const selectableShipments = useMemo(() => list.filter(item => isShipmentSelectable(item, orgName)), [list, orgName]);

  const isAllSelectableChecked = useMemo(() => {
    if (selectableShipments.length === 0) return false;
    return selectableShipments.every(item => selectedShipmentIds.includes(item._id));
  }, [selectableShipments, selectedShipmentIds]);

  const isSomeSelectableChecked = useMemo(() => {
    if (isAllSelectableChecked || selectableShipments.length === 0) return false;
    return selectableShipments.some(item => selectedShipmentIds.includes(item._id));
  }, [isAllSelectableChecked, selectableShipments, selectedShipmentIds]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Top Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        sx={{ mb: 1, flexShrink: 0 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '8px',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LocalShippingOutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.1 }}>
              Unbilled Shipments
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
              Showing {list.length} of {totalCount} total shipments
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            label={`${selectedShipmentIds.length} Selected`}
            color={selectedShipmentIds.length > 0 ? 'primary' : 'default'}
            variant={selectedShipmentIds.length > 0 ? 'filled' : 'outlined'}
            sx={{ height: 24, fontSize: '0.72rem', fontWeight: 600 }}
          />
        </Stack>
      </Stack>

      {/* Filter Toolbar with Ultra-Thin 2.5px Horizontal Scrollbar */}
      <Box
        sx={{
          p: '6px 10px',
          mb: 1,
          borderRadius: '10px',
          bgcolor: alpha(theme.palette.primary.main, 0.025),
          border: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexWrap: 'nowrap',
          alignItems: 'center',
          gap: 1.25,
          flexShrink: 0,
          overflowX: 'auto',
          overflowY: 'hidden',
          whiteSpace: 'nowrap',
          scrollbarWidth: 'thin',
          scrollbarColor: `${alpha(theme.palette.text.primary, 0.18)} transparent`,
          '&::-webkit-scrollbar': {
            height: '2.5px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(theme.palette.text.primary, 0.18),
            borderRadius: '4px',
            transition: 'background-color 0.2s ease',
          },
          '&:hover::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(theme.palette.text.primary, 0.28),
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: alpha(theme.palette.text.primary, 0.45),
          },
        }}
      >
        <Box sx={{ width: '270px', minWidth: '240px', flexShrink: 0 }}>
          <FilterSearch
            onSelectChange={onSearchSelectChange}
            list={NonAccountSearchFilterList}
            selectValue={filterSelectValue}
            textValue={filterText}
            onTextChange={onSearchTextChange}
            onTextSearch={onSearchTextSubmit}
            onTextClear={onTextClear}
          />
        </Box>

        <Box sx={{ minWidth: '220px', flexShrink: 0 }}>
          <DateRangePicker onChange={onDateChange} value={dateRange} />
        </Box>

        <Box sx={{ minWidth: '150px', flexShrink: 0 }}>
          <FilterSelect id="system" label="System" value={system} options={systemList} onChange={onSystemFilter} />
        </Box>

        {orgName !== 'Cargo Solutions Network' && (
          <Box sx={{ minWidth: '160px', flexShrink: 0 }}>
            <FilterSelect
              id="shipmenttype"
              label="Shipment Type"
              value={shipmentType}
              options={ShipmentTypeList}
              onChange={onShipmentTypeFilter}
            />
          </Box>
        )}

        <SbButton
          variant="outlined"
          size="small"
          onClick={onClearFilters}
          startIcon={<FilterAltOffOutlinedIcon fontSize="small" />}
          sx={{
            minWidth: 105,
            height: 38,
            borderRadius: '8px',
            fontSize: '0.75rem',
            textTransform: 'none',
            flexShrink: 0,
          }}
        >
          Clear
        </SbButton>
      </Box>

      {/* Select All Bar */}
      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          mb: 1,
          borderRadius: '8px',
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Checkbox
            size="small"
            checked={isAllSelectableChecked}
            indeterminate={isSomeSelectableChecked}
            disabled={selectableShipments.length === 0}
            onChange={onSelectAllShipments}
            sx={{ p: 0.25 }}
          />
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary' }}>
            Select all on this page ({selectableShipments.length} selectable)
          </Typography>
        </Stack>

        <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>
          Page {currentPage} of {Math.max(1, Math.ceil(totalCount / (perPage || 10)))}
        </Typography>
      </Box>

      {/* Main Shipment Cards List */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          pr: 0.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {loading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>Loading shipments...</Typography>
          </Box>
        ) : list.length === 0 ? (
          <Box
            sx={{
              py: 8,
              textAlign: 'center',
              border: `1px dashed ${theme.palette.divider}`,
              borderRadius: '12px',
              p: 3,
            }}
          >
            <Typography sx={{ color: 'text.secondary', fontSize: '0.88rem', fontWeight: 500 }}>
              No unbilled shipments found matching the criteria.
            </Typography>
          </Box>
        ) : (
          list.map(shipment => (
            <ShipmentCard
              key={shipment._id}
              shipment={shipment}
              isSelected={selectedShipmentIds.includes(shipment._id)}
              isExpanded={Boolean(expandedShipments[shipment._id])}
              orgName={orgName}
              currencySymbol={primaryCurrencySymbol}
              onToggleSelect={onToggleSelectShipment}
              onToggleExpand={onToggleExpandShipment}
            />
          ))
        )}
      </Box>

      {/* Pagination Footer */}
      <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, pt: 0.5, flexShrink: 0 }}>
        <TablePagination
          component="div"
          count={totalCount}
          page={currentPage - 1}
          rowsPerPage={perPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50]}
          sx={{
            '& .MuiTablePagination-toolbar': { minHeight: 44, p: 0 },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: '0.78rem',
            },
          }}
        />
      </Box>
    </Box>
  );
});

export default NonAccountShipmentSection;
